import {
  useAgentCapabilities,
  useHarnessCapabilities,
} from "@/api/harnessCapabilities";
import { ConnectAgentButton } from "@/components/harness/ConnectAgentButton";
import { useAgentConnect } from "@/components/harness/useAgentConnect";
import { EffortPicker } from "@/components/MessageInput/components/EffortPicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AGENT_IDS,
  AGENT_META,
  AGENT_ORDER,
  AgentCapability,
  AgentId,
  agentOf,
  findAgent,
  isCohesiveHarness,
} from "@/constants/agents";
import {
  defaultEffortFor,
  defaultModelFor,
  harnessIcon,
  harnessLabel,
  harnessSeatVisible,
} from "@/constants/harnesses";
import { reasoningEffortOptions } from "@/pages/canvas/hooks/useCanvasReasoningEffort";
import {
  automationAgentSelection,
  type AutomationHarnessSelection,
} from "@/panes/automation/automationHarness";
import { Flags } from "@/providers/LaunchDarklyProvider";
import { HARNESS_IDS, HarnessCapabilityModel } from "@cohesive/api-client";
import { useFlags } from "launchdarkly-react-client-sdk";

/** Stands in for "no pinned model" or "no tier" — a Select item's value cannot be empty. */
const DEFAULT_VALUE = "__default__";

/** The select value of a stamp that is no agent (opencode, codex on the platform). */
const LEGACY_VALUE = "__legacy__";

const ICON_CLASS = "size-3.5 shrink-0 text-n-50";

/** An agent row: name, what it runs on, and Connect where the subscription isn't connected. */
function AgentOption({
  agent,
  capability,
  expanded,
  onConnect,
}: {
  agent: AgentId;
  capability: AgentCapability | undefined;
  /** The open list says what each runs on; the trigger names the agent alone. */
  expanded: boolean;
  onConnect: (agent: AgentId) => void;
}) {
  const { Logo, label, plan } = AGENT_META[agent];
  const sub = plan
    ? `Your ${plan} subscription`
    : AGENT_META[AGENT_IDS.COHESIVE].cardSubline;
  const needsConnect = agent !== AGENT_IDS.COHESIVE && !capability?.connected;
  return (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-2">
          <Logo className={ICON_CLASS} />
          <span className="truncate">{label}</span>
        </span>
        {expanded && (
          <span className="text-xs leading-snug text-muted-foreground">
            {sub}
          </span>
        )}
      </span>
      {expanded && needsConnect && (
        <span
          // Inside a select row: the press must not pick the row as well.
          onPointerDown={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
        >
          <ConnectAgentButton onClick={() => onConnect(agent)} />
        </span>
      )}
    </span>
  );
}

/** One row: the model, by its alias where it has one. */
function ModelOption({
  model,
  aliasLabel,
  prefix,
}: {
  model: HarnessCapabilityModel;
  aliasLabel?: string;
  prefix?: string;
}) {
  const label = aliasLabel ?? model.label;
  return (
    <span className="truncate">{prefix ? `${prefix} (${label})` : label}</span>
  );
}

/**
 * Whether the agent row belongs on the form at all. Gated the way the composer's chip is: with
 * the runtime off Cohesive is the only choice and a one-option select is noise. An automation
 * already on a harness states it either way.
 *
 * Its own hook because the label is the caller's — the two surfaces use different label
 * primitives — and it is the caller's gate that decides: the fields render whatever they are
 * given, so a caller that skipped it would show an orphan label over a one-option select.
 */
export function useAutomationHarnessVisible(
  selection: AutomationHarnessSelection,
): boolean {
  const flags = useFlags<Flags>();
  const { data: capabilities } = useHarnessCapabilities();
  return harnessSeatVisible(
    selection.harness,
    !!flags.harnessRuntime,
    capabilities,
  );
}

/**
 * Which agent a schedule's runs are stamped with, and its dial: Cohesive's effort tier, or a
 * subscription's model and thinking. Plain selects rather than the composer's pills: this is a
 * settings form, and "Runs on" sits alongside Location and Schedule as one more row. The hint
 * under it says what the choice means for the runs.
 *
 * A schedule's agent is changeable — the stamp lands on each run, not on the schedule — so
 * unlike a chat there is nothing to lock. A stamp that is no agent (an OpenCode automation) or a
 * retired model is still listed while it is the one selected — the server revalidates the stored
 * pair on every PATCH, so it fails every edit until replaced, and a value the form hid would be
 * one the user could not find to replace.
 */
export function AutomationHarnessFields({
  value,
  onChange,
  disabled,
  defaultsToPreferredModel,
}: {
  value: AutomationHarnessSelection;
  onChange: (next: AutomationHarnessSelection) => void;
  disabled?: boolean;
  /** Create pins the user's preferred model when none is picked; an edit clears to the catalog's. */
  defaultsToPreferredModel?: boolean;
}) {
  const flags = useFlags<Flags>();
  const { data: agents } = useAgentCapabilities();
  const agentConnect = useAgentConnect();

  const agentId = agentOf(value.harness);
  const agent = findAgent(agents, agentId);
  // A legacy harness that is no agent's own (opencode) is stated as itself.
  const isAgent =
    isCohesiveHarness(value.harness) ||
    (!!agent && agent.harness === value.harness);
  const selectValue = isAgent ? agentId : LEGACY_VALUE;
  const LegacyIcon = harnessIcon(value.harness);

  const onSubscription = isAgent && agentId !== AGENT_IDS.COHESIVE;
  const catalogModels = onSubscription ? (agent?.models ?? []) : [];
  // A pin the catalog no longer lists is listed anyway, for the same reason a legacy stamp is.
  const models =
    value.model && !catalogModels.some((m) => m.id === value.model)
      ? [...catalogModels, { id: value.model, label: value.model }]
      : catalogModels;
  const aliasLabel = (modelId: string | undefined) =>
    agent?.aliases?.find((a) => a.model_id === modelId)?.label;
  const defaultModel = catalogModels.find(
    (m) => m.id === defaultModelFor(agent, defaultsToPreferredModel),
  );
  // Efforts are the run model's: the pin, else the harness default an unpinned run resolves.
  const effortsFor = (model: string | undefined) =>
    catalogModels.find((m) => m.id === (model ?? defaultModel?.id))?.efforts ??
    [];
  const runModel = value.model ?? defaultModel?.id;
  const efforts = effortsFor(runModel);
  // Unset, or one the model no longer offers, is the Default row: runs take the owner's default.
  const effort = efforts.some((e) => e.id === value.effort)
    ? value.effort
    : undefined;

  const cohesive = findAgent(agents, AGENT_IDS.COHESIVE);
  const tierIds = reasoningEffortOptions(flags);
  const tiers = tierIds
    .map((id) => cohesive?.tiers?.find((tier) => tier.id === id))
    .filter((tier) => !!tier);
  const defaultTier = cohesive?.tiers?.find(
    (tier) => tier.id === cohesive.default_tier_id,
  );
  // The tier means the same on either loop, so Cohesive stays `cohesive` and follows the flag.
  const tierable = agentId === AGENT_IDS.COHESIVE && tiers.length > 0;
  const tier = tiers.some((t) => t.id === value.effort)
    ? value.effort
    : undefined;

  const subscriptionMeta = onSubscription ? AGENT_META[agentId] : undefined;
  const hint = !isAgent
    ? "Every run this automation starts uses this agent."
    : subscriptionMeta
      ? agent?.connected
        ? `Runs on your ${subscriptionMeta.providerName} subscription${agent.account_email ? ` (${agent.account_email})` : ""}. If it’s disconnected, runs pause rather than moving to Cohesive.`
        : `${subscriptionMeta.providerName} isn’t connected. Connect your ${subscriptionMeta.plan} plan for this automation to run.`
      : "Every run this automation starts uses this agent.";

  return (
    <div className="flex flex-col gap-2">
      <Select
        value={selectValue}
        disabled={disabled}
        onValueChange={(next) => {
          if (next === LEGACY_VALUE) return;
          const target = findAgent(agents, next as AgentId);
          // No model, so the automation tracks whatever the harness defaults to. A schedule
          // provisions a fresh sandbox per run and outlives the model list it was made under.
          if (target) onChange(automationAgentSelection(target));
        }}
      >
        <SelectTrigger className="h-9" aria-label="Runs on">
          <SelectValue placeholder="Runs on">
            {isAgent ? (
              <AgentOption
                agent={agentId}
                capability={agent}
                expanded={false}
                onConnect={agentConnect.openConnect}
              />
            ) : (
              <span className="flex min-w-0 items-center gap-2">
                {LegacyIcon && <LegacyIcon className={ICON_CLASS} />}
                <span className="truncate">{harnessLabel(value.harness)}</span>
              </span>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {AGENT_ORDER.map((id) => {
            const capability = findAgent(agents, id);
            if (!capability && !(id === AGENT_IDS.COHESIVE && !agents)) {
              return null;
            }
            return (
              <SelectItem
                key={id}
                value={id}
                // The row's text fills it, so Connect sits at its right edge.
                className="[&>span:last-child]:min-w-0 [&>span:last-child]:flex-1"
              >
                <AgentOption
                  agent={id}
                  capability={capability}
                  expanded
                  onConnect={agentConnect.openConnect}
                />
              </SelectItem>
            );
          })}
          {!isAgent && (
            <SelectItem value={LEGACY_VALUE}>
              <span className="flex min-w-0 items-center gap-2">
                {LegacyIcon && <LegacyIcon className={ICON_CLASS} />}
                <span className="truncate">{harnessLabel(value.harness)}</span>
              </span>
            </SelectItem>
          )}
        </SelectContent>
      </Select>

      {tierable && (
        <Select
          value={tier ?? DEFAULT_VALUE}
          disabled={disabled}
          onValueChange={(next) =>
            onChange({
              harness: HARNESS_IDS.COHESIVE,
              effort: next === DEFAULT_VALUE ? undefined : next,
            })
          }
        >
          <SelectTrigger className="h-9" aria-label="Effort">
            <SelectValue placeholder="Effort" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={DEFAULT_VALUE}>
              Effort: Default{defaultTier ? ` (${defaultTier.label})` : ""}
            </SelectItem>
            {tiers.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                Effort: {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {models.length > 0 && (
        <div className="flex gap-2">
          <Select
            value={value.model ?? DEFAULT_VALUE}
            disabled={disabled}
            onValueChange={(next) => {
              const model = next === DEFAULT_VALUE ? undefined : next;
              // Kept only where the new model offers it, as the server does.
              const keepEffort = effortsFor(model).some(
                (e) => e.id === value.effort,
              );
              onChange({
                harness: value.harness,
                model,
                effort: keepEffort ? value.effort : undefined,
              });
            }}
          >
            <SelectTrigger className="h-9 min-w-0 flex-1" aria-label="Model">
              <SelectValue placeholder="Model" />
            </SelectTrigger>
            <SelectContent>
              {/* First and preselected: an automation left alone follows the platform default
                rather than pinning whichever model that resolved to the day it was made. */}
              <SelectItem value={DEFAULT_VALUE}>
                {defaultModel ? (
                  <ModelOption
                    model={defaultModel}
                    aliasLabel={aliasLabel(defaultModel.id)}
                    prefix="Default"
                  />
                ) : (
                  "Default"
                )}
              </SelectItem>
              {models.map((model) => (
                <SelectItem key={model.id} value={model.id}>
                  <ModelOption model={model} />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {efforts.length > 0 && (
            <EffortPicker
              efforts={efforts}
              effort={effort}
              defaultEffort={defaultEffortFor(agent, runModel)}
              onSelectDefault={() => {
                if (value.effort !== undefined) {
                  onChange({ ...value, effort: undefined });
                }
              }}
              side="bottom"
              disabled={disabled}
              className="h-9 shrink-0 gap-2 rounded-md border border-input bg-input-background px-3 font-normal"
              onSelect={(next) => onChange({ ...value, effort: next })}
            />
          )}
        </div>
      )}

      <p className="text-xs text-n-40">{hint}</p>
      {agentConnect.dialog}
    </div>
  );
}
